/**
 * Hues.js — Virtual DOM
 * -----------------------------------------------------------------------------
 * A lightweight virtual DOM implementation that supports:
 *
 *   • `h()` / `createElement()`  — create vnodes
 *   • Text, comment, fragment, and element vnodes
 *   • Component vnodes (functional & stateful)
 *   • Keys, props, children diffing
 *   • `mount()`, `patch()` and `unmount()` operations
 *   • Refs (`ref` prop on DOM nodes)
 *   • Lifecycle hooks invocation from the renderer
 *
 * The diffing is intentionally simple (synchronous, single-threaded) but covers
 * every case needed for production-grade UIs: keyed list reconciliation,
 * attribute patching, event listener management, style merging, etc.
 *
 * © Death Legion — Hues.js
 */

import { effect, Ref, isRef, unref } from './reactivity'
import { ComponentInstance, currentInstance, setCurrentInstance, getCurrentInstance, invokeHook } from './component'

// ----- Types ------------------------------------------------------------------

export type VNodeType = string | number | Component | typeof Fragment | typeof Comment | symbol
export interface VNode {
  type: VNodeType
  props: Record<string, any> | null
  children: VNode[] | string | number | null
  key: string | number | null
  el: Node | null
  component: ComponentInstance | null
  ref: ((el: any) => void) | Ref<any> | string | null
  __isVNode: true
}

export type Component<P = any> = {
  (props?: P, ctx?: any): VNode
  displayName?: string
  setup?: (props: P, ctx: ComponentContext) => any
  props?: Record<string, PropDef>
  emits?: string[]
  name?: string
  template?: string
  // Optional lifecycle hooks attached at definition time
  beforeCreate?: () => void
  created?: () => void
  beforeMount?: () => void
  mounted?: () => void
  beforeUpdate?: () => void
  updated?: () => void
  beforeUnmount?: () => void
  unmounted?: () => void
}

export interface ComponentContext {
  attrs: Record<string, any>
  slots: Record<string, () => VNode[]>
  emit: (event: string, ...args: any[]) => void
  expose: (exposed: Record<string, any>) => void
}

export interface PropDef {
  type?: any
  default?: any
  required?: boolean
  validator?: (v: any) => boolean
}

export const Fragment = Symbol('Fragment')
export const Comment = Symbol('Comment')
export const Text = Symbol('Text')

// ----- createElement / h ------------------------------------------------------

export function h(
  type: VNodeType,
  props?: Record<string, any> | null,
  children?: VNode[] | string | number | any[] | null,
): VNode {
  const key = props && props.key != null ? props.key : null
  const ref = props && (props.ref ?? null)
  const cleanedProps = props ? { ...props } : null
  if (cleanedProps) {
    delete cleanedProps.key
    delete cleanedProps.ref
  }
  let normalizedChildren: VNode[] | string | number | null = null
  if (children == null) {
    normalizedChildren = null
  } else if (typeof children === 'string' || typeof children === 'number') {
    normalizedChildren = String(children)
  } else if (Array.isArray(children)) {
    normalizedChildren = children.map(normalizeChild).filter(Boolean) as VNode[]
  } else if ((children as any).__isVNode) {
    normalizedChildren = [children as VNode]
  } else if (typeof children === 'function') {
    // A single function child is treated as a dynamic-text or slot-style
    // child. We construct a Text vnode directly (rather than calling h()
    // again) to avoid infinite recursion.
    normalizedChildren = [{
      type: Text,
      props: null,
      children: children as any,
      key: null,
      el: null,
      component: null,
      ref: null,
      __isVNode: true,
    }]
  } else {
    normalizedChildren = String(children)
  }
  return { type, props: cleanedProps, children: normalizedChildren, key, el: null, component: null, ref, __isVNode: true }
}

function normalizeChild(child: any): VNode | null {
  if (child == null || child === false || child === true) return null
  if ((child as any).__isVNode) return child
  if (typeof child === 'string' || typeof child === 'number') {
    return h(Text, null, String(child))
  }
  if (isRef(child)) {
    return h(Text, null, () => String(unref(child)))
  }
  if (typeof child === 'function') {
    return h(Text, null, child as any)
  }
  return h(Text, null, String(child))
}

export function isVNode(v: any): v is VNode {
  return v && v.__isVNode === true
}

// ----- Renderer ---------------------------------------------------------------

const eventRE = /^on([A-Z])/
const svgNS = 'http://www.w3.org/2000/svg'

export function mount(vnode: VNode, container: Node, anchor: Node | null = null): void {
  const { type } = vnode
  if (type === Text) {
    let val: any = vnode.children
    if (typeof val === 'function') {
      const _v = val
      effect(() => {
        const text = String(_v())
        if (vnode.el) (vnode.el as Text).nodeValue = text
        else vnode.el = document.createTextNode(text)
      })
      if (!vnode.el) vnode.el = document.createTextNode('')
    } else {
      vnode.el = document.createTextNode(String(val))
    }
    container.insertBefore(vnode.el!, anchor)
    return
  }
  if (type === Comment) {
    vnode.el = document.createComment('')
    container.insertBefore(vnode.el!, anchor)
    return
  }
  if (type === Fragment) {
    const fragment = document.createDocumentFragment()
    if (Array.isArray(vnode.children)) {
      for (const child of vnode.children) mount(child, fragment)
    }
    vnode.el = (fragment.firstChild as Node) || document.createComment('')
    container.insertBefore(fragment, anchor)
    return
  }
  if (typeof type === 'string') {
    const el = (vnode.el = type === 'svg'
      ? document.createElementNS(svgNS, type)
      : document.createElement(type))
    patchProps(null, vnode.props, el)
    if (typeof vnode.children === 'string') {
      el.textContent = vnode.children
    } else if (typeof vnode.children === 'number') {
      el.textContent = String(vnode.children)
    } else if (Array.isArray(vnode.children)) {
      for (const child of vnode.children) mount(child, el)
    } else if (typeof vnode.children === 'function') {
      // dynamic text from a function (children is a getter)
      const _c = vnode.children
      effect(() => {
        const text = String(_c())
        el.textContent = text
      })
    }
    setRef(vnode, el)
    container.insertBefore(el, anchor)
    return
  }
  if (typeof type === 'object' || typeof type === 'function') {
    mountComponent(vnode, container, anchor)
    return
  }
}

function setRef(vnode: VNode, el: Node): void {
  if (!vnode.ref) return
  if (typeof vnode.ref === 'function') vnode.ref(el)
  else if (isRef(vnode.ref)) (vnode.ref as Ref<any>).value = el
}

function patchProps(oldProps: Record<string, any> | null, newProps: Record<string, any> | null, el: Element): void {
  oldProps = oldProps || {}
  newProps = newProps || {}
  // Remove stale props
  for (const key in oldProps) {
    if (key === 'key' || key === 'ref') continue
    if (!(key in newProps)) {
      if (eventRE.test(key)) {
        el.removeEventListener(key.slice(2).toLowerCase(), oldProps[key])
      } else if (key === 'class') {
        el.className = ''
      } else if (key === 'style') {
        el.removeAttribute('style')
      } else {
        el.removeAttribute(key)
      }
    }
  }
  // Apply new props
  for (const key in newProps) {
    if (key === 'key' || key === 'ref') continue
    const value = newProps[key]
    if (eventRE.test(key)) {
      const eventName = key.slice(2).toLowerCase()
      if (oldProps[key] !== value) {
        if (oldProps[key]) el.removeEventListener(eventName, oldProps[key])
        if (typeof value === 'function') el.addEventListener(eventName, value)
      }
    } else if (key === 'class') {
      // SVG elements have a read-only `className` (SVGAnimatedString), so we
      // must use setAttribute('class', ...) for them.
      if (el instanceof SVGElement) el.setAttribute('class', stringifyClass(value))
      else el.className = stringifyClass(value)
    } else if (key === 'style') {
      if (typeof value === 'string') el.style.cssText = value
      else if (typeof value === 'object' && value !== null) {
        for (const styleKey in value) {
          ;(el.style as any)[styleKey] = value[styleKey]
        }
      }
    } else if (key === 'innerHTML' || key === 'textContent') {
      ;(el as any)[key] = value
    } else if (key === 'value' && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT')) {
      ;(el as HTMLInputElement).value = value ?? ''
    } else if (key === 'disabled' || key === 'checked' || key === 'selected' || key === 'readonly') {
      if (value) el.setAttribute(key, '')
      else el.removeAttribute(key)
    } else if (value !== false && value != null) {
      el.setAttribute(key, value === true ? '' : String(value))
    }
  }
}

function stringifyClass(value: any): string {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return value.map(stringifyClass).filter(Boolean).join(' ')
  if (typeof value === 'object') {
    return Object.keys(value).filter((k) => value[k]).join(' ')
  }
  return ''
}

// ----- Component mounting -----------------------------------------------------

function mountComponent(vnode: VNode, container: Node, anchor: Node | null): void {
  const comp = vnode.type as Component
  // If createApp.mount() pre-attached an instance (e.g. for the root component),
  // reuse it instead of creating a new one — this preserves app-level provides.
  const parentInstance = getCurrentInstance()
  const existing = vnode.component as ComponentInstance | null
  const instance: ComponentInstance = existing ?? {
    type: comp,
    props: resolveProps(comp, vnode.props || {}),
    attrs: {},
    slots: {},
    state: {},
    ctx: {
      attrs: {},
      slots: resolveSlots(vnode.children),
      emit: (event, ...args) => {
        const handler = (vnode.props || {})[`on${event[0].toUpperCase()}${event.slice(1)}`]
        if (Array.isArray(handler)) handler.forEach((h) => h(...args))
        else if (typeof handler === 'function') handler(...args)
      },
      expose: (exposed) => {
        instance.exposed = exposed
      },
    },
    vnode,
    subTree: null,
    isMounted: false,
    effects: [],
    exposed: null,
    provides: parentInstance ? Object.create(parentInstance.provides) : {},
  }
  vnode.component = instance

  setCurrentInstance(instance)
  invokeHook(instance, 'beforeCreate')
  // Resolve state via setup() if present
  if (comp.setup) {
    const setupResult = comp.setup(instance.props, instance.ctx)
    if (setupResult && typeof setupResult === 'object') {
      instance.state = setupResult
    } else if (typeof setupResult === 'function') {
      instance.render = setupResult as () => VNode
    }
  }
  // If no render returned from setup, use the template or default render
  if (!instance.render) {
    instance.render = () => (comp as any)(instance.props, instance.ctx)
  }
  invokeHook(instance, 'created')

  // Mount sub-tree inside an effect so updates are reactive. CRITICAL: we
  // must re-establish `currentInstance` to THIS instance around every render
  // so that child components mounted during this render inherit our provides
  // chain via getCurrentInstance().
  const updateEffect = effect(() => {
    setCurrentInstance(instance)
    try {
      const subTree = instance.render!()
      if (!instance.isMounted) {
        invokeHook(instance, 'beforeMount')
        mount(subTree, container, anchor)
        instance.subTree = subTree
        instance.isMounted = true
        invokeHook(instance, 'mounted')
      } else {
        invokeHook(instance, 'beforeUpdate')
        patch(instance.subTree!, subTree, container, anchor)
        instance.subTree = subTree
        invokeHook(instance, 'updated')
      }
    } finally {
      setCurrentInstance(parentInstance)
    }
  }, { scheduler: (run) => Promise.resolve().then(run) })
  instance.effects.push(updateEffect)

  // Note: do NOT call setCurrentInstance(null) here. The effect's finally
  // block already restored currentInstance to `parentInstance`, which is
  // correct — the parent's effect is still running (we're inside its mount),
  // and any sibling components mounted next need to see the parent as the
  // current instance.
}

function resolveProps(comp: Component, rawProps: Record<string, any>): Record<string, any> {
  const declared = comp.props
  const result: Record<string, any> = {}
  if (!declared) {
    Object.assign(result, rawProps)
    return result
  }
  for (const key in declared) {
    const def = declared[key]
    if (key in rawProps) {
      const value = rawProps[key]
      if (def.validator && !def.validator(value)) {
        console.warn(`[Hues warn] Invalid prop "${key}" for component ${comp.name || ''}`)
      }
      result[key] = value
    } else if (def.default !== undefined) {
      result[key] = typeof def.default === 'function' ? def.default() : def.default
    } else if (def.required) {
      console.warn(`[Hues warn] Missing required prop "${key}" for component ${comp.name || ''}`)
    }
  }
  return result
}

function resolveSlots(children: VNode['children']): Record<string, () => VNode[]> {
  const slots: Record<string, () => VNode[]> = {}
  if (!Array.isArray(children)) return slots
  slots.default = () => children
  for (const child of children) {
    const slotName = child.props?.slot
    if (slotName) {
      slots[slotName] = () => [child]
    }
  }
  return slots
}

// ----- Patch ------------------------------------------------------------------

export function patch(oldVNode: VNode, newVNode: VNode, container: Node, anchor: Node | null = null): void {
  if (oldVNode === newVNode) return
  // Different types → unmount + mount
  if (oldVNode.type !== newVNode.type) {
    unmount(oldVNode)
    mount(newVNode, container, anchor)
    return
  }
  // Same type
  newVNode.el = oldVNode.el
  if (oldVNode.type === Text) {
    let val: any = newVNode.children
    if (typeof val === 'function') {
      // The effect from mount already handles this; nothing to do.
      return
    }
    if ((oldVNode.el as Text).nodeValue !== String(val)) {
      ;(oldVNode.el as Text).nodeValue = String(val)
    }
    return
  }
  if (oldVNode.type === Comment) return
  if (oldVNode.type === Fragment) {
    patchChildren(oldVNode, newVNode, container)
    return
  }
  if (typeof oldVNode.type === 'string') {
    const el = oldVNode.el as Element
    patchProps(oldVNode.props, newVNode.props, el)
    patchChildren(oldVNode, newVNode, el)
    return
  }
  if (typeof oldVNode.type === 'object' || typeof oldVNode.type === 'function') {
    // Component re-render: just update props, the effect will re-run
    const instance = oldVNode.component!
    instance.props = resolveProps(oldVNode.type as Component, newVNode.props || {})
    newVNode.component = instance
    instance.vnode = newVNode
    return
  }
}

function patchChildren(oldVNode: VNode, newVNode: VNode, container: Node): void {
  const oldChildren = Array.isArray(oldVNode.children) ? oldVNode.children : []
  const newChildren = Array.isArray(newVNode.children) ? newVNode.children : []
  if (typeof newVNode.children === 'string') {
    if (oldChildren.length) oldChildren.forEach(unmount)
    container.textContent = newVNode.children
    return
  }
  // Keyed reconciliation
  const oldKeyed = new Map<string | number, VNode>()
  for (const c of oldChildren) {
    if (c.key != null) oldKeyed.set(c.key, c)
  }
  let oldStart = 0
  let oldEnd = oldChildren.length - 1
  let newStart = 0
  let newEnd = newChildren.length - 1
  while (oldStart <= oldEnd && newStart <= newEnd) {
    const oldChild = oldChildren[oldStart]
    const newChild = newChildren[newStart]
    if (oldChild.key !== newChild.key) break
    patch(oldChild, newChild, container)
    oldStart++
    newStart++
  }
  while (oldStart <= oldEnd && newStart <= newEnd) {
    const oldChild = oldChildren[oldEnd]
    const newChild = newChildren[newEnd]
    if (oldChild.key !== newChild.key) break
    patch(oldChild, newChild, container)
    oldEnd--
    newEnd--
  }
  // New children to add
  if (oldStart > oldEnd) {
    const anchor = newChildren[newEnd + 1]?.el ?? null
    while (newStart <= newEnd) {
      mount(newChildren[newStart], container, anchor)
      newStart++
    }
    return
  }
  // Old children to remove
  if (newStart > newEnd) {
    while (oldStart <= oldEnd) {
      unmount(oldChildren[oldStart])
      oldStart++
    }
    return
  }
  // Unknown moves — simple fallback: unmount all old, mount all new
  for (const c of oldChildren.slice(oldStart, oldEnd + 1)) unmount(c)
  for (const c of newChildren.slice(newStart, newEnd + 1)) mount(c, container)
}

export function unmount(vnode: VNode): void {
  if (vnode.type === Text || vnode.type === Comment) {
    vnode.el?.parentNode?.removeChild(vnode.el)
    return
  }
  if (vnode.type === Fragment) {
    if (Array.isArray(vnode.children)) vnode.children.forEach(unmount)
    return
  }
  if (vnode.component) {
    const instance = vnode.component
    invokeHook(instance, 'beforeUnmount')
    instance.effects.forEach((e) => e.stop?.())
    if (instance.subTree) unmount(instance.subTree)
    invokeHook(instance, 'unmounted')
    return
  }
  if (Array.isArray(vnode.children)) vnode.children.forEach(unmount)
  vnode.el?.parentNode?.removeChild(vnode.el)
}

// ----- Render API -------------------------------------------------------------

export function render(vnode: VNode | null, container: HTMLElement): void {
  const prev = (container as any).__hues_vnode as VNode | undefined
  if (prev) {
    if (vnode) patch(prev, vnode, container)
    else unmount(prev)
  } else if (vnode) {
    mount(vnode, container)
  }
  ;(container as any).__hues_vnode = vnode || null
}
